// src/api/features/leads/api.ts
import { request } from "@/api/client";
import { PATHS } from "@/api/constants";

import type {
  PaginatedLeads,
  LeadListItem,
  LeadWithProducts,
  CreateLeadBody,
  UpdateLeadBody,
  ListLeadsQuery,
  LeadProductWithMeta,
  TransferLeadProductBody,
  LeadProductMilestone,
  CreateLeadProductMilestoneBody,
  UpdateLeadProductMilestoneBody,
  LeadProductVisaDetail,
  UpsertLeadProductVisaDetailBody,
} from "./types";

/* =========================
 * Helpers
 * ========================= */

type ApiEnvelope<T> = { ok: boolean; message?: string; data: T; meta?: any };

function unwrap<T>(res: any): T {
  if (res && typeof res === "object" && "ok" in res && "data" in res) {
    return (res as ApiEnvelope<T>).data;
  }
  return res as T;
}

function qs(params: Record<string, unknown>): string {
  const u = new URLSearchParams();

  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    u.set(k, String(v));
  }

  const s = u.toString();
  return s ? `?${s}` : "";
}

/* =========================
 * URLs (constants)
 * ========================= */

const LEADS_BASE = PATHS.leads.base;

/* =========================
 * Endpoints — CORE LEADS
 * ========================= */

/** 🔹 Create lead (and its product rows) — DEEP payload */
export async function createLead(
  body: CreateLeadBody
): Promise<LeadWithProducts> {
  const dto: any = { ...body };

  // Light normalization / trimming
  if (dto.firstName != null) dto.firstName = String(dto.firstName).trim();
  if (dto.lastName != null) dto.lastName = String(dto.lastName).trim();
  if (dto.email != null) dto.email = String(dto.email).trim();
  if (dto.phone != null) dto.phone = String(dto.phone).trim();
  if (dto.notes != null) dto.notes = String(dto.notes).trim();

  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.debug("[createLead] payload →", dto);
  }

  const res = await request<
    LeadWithProducts | ApiEnvelope<LeadWithProducts>
  >({
    method: "POST",
    url: LEADS_BASE,
    data: dto,
  });

  return unwrap<LeadWithProducts>(res);
}

/** 🔹 List leads (paginated, base list rows) */
export async function listLeads(
  query: ListLeadsQuery = {}
): Promise<PaginatedLeads> {
  const safePage = Math.max(1, Number(query.page ?? 1));
  const rawPageSize = Number(query.pageSize ?? 10);
  const safePageSize = Math.max(
    1,
    Math.min(100, Number.isFinite(rawPageSize) ? rawPageSize : 10)
  );

  const url = `${LEADS_BASE}${qs({
    page: safePage,
    pageSize: safePageSize,
    q: query.q,
    firstName: query.firstName,
    lastName: query.lastName,
    email: query.email,
    phone: query.phone,
    product: query.product,
    status: query.status,
    source: query.source,
    priority: query.priority,
    assignedToUserId:
      typeof query.assignedToUserId === "number" &&
      Number.isFinite(query.assignedToUserId)
        ? query.assignedToUserId
        : undefined,
    leadDateFrom: query.leadDateFrom,
    leadDateTo: query.leadDateTo,
  })}`;

  const res = await request<
    PaginatedLeads | (ApiEnvelope<LeadListItem[]> & { meta?: any })
  >({
    method: "GET",
    url,
  });

  // If server returns { ok, data, meta }
  if (res && typeof res === "object" && "ok" in res && "data" in res) {
    const r = res as ApiEnvelope<LeadListItem[]> & { meta?: any };

    const items = Array.isArray(r.data) ? r.data : [];
    const meta = r.meta ?? {};

    return {
      items,
      page: Number(meta.page ?? safePage),
      pageSize: Number(meta.pageSize ?? safePageSize),
      total: Number(meta.total ?? items.length ?? 0),
    };
  }

  // Otherwise assume backend already returns PaginatedLeads
  return res as PaginatedLeads;
}

/** 🔹 Get lead by id — DEEP (with products + transfers) */
export async function getLead(
  id: number | string
): Promise<LeadWithProducts> {
  const res = await request<
    LeadWithProducts | ApiEnvelope<LeadWithProducts>
  >({
    method: "GET",
    url: `${LEADS_BASE}/${id}`,
  });

  return unwrap<LeadWithProducts>(res);
}

/** 🔹 Update lead — DEEP update (lead + products) */
export async function updateLead(
  id: number | string,
  body: UpdateLeadBody
): Promise<LeadWithProducts> {
  const dto: any = { ...body };

  if (dto.firstName != null) dto.firstName = String(dto.firstName).trim();
  if (dto.lastName != null) dto.lastName = String(dto.lastName).trim();
  if (dto.email != null) dto.email = String(dto.email).trim();
  if (dto.phone != null) dto.phone = String(dto.phone).trim();
  if (dto.notes != null) dto.notes = String(dto.notes).trim();

  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.debug("[updateLead] id=", id, "payload →", dto);
  }

  const res = await request<
    LeadWithProducts | ApiEnvelope<LeadWithProducts>
  >({
    method: "PUT",
    url: `${LEADS_BASE}/${id}`,
    data: dto,
  });

  return unwrap<LeadWithProducts>(res);
}

/** 🔹 Soft delete lead */
export async function deleteLead(
  id: number | string
): Promise<{ message: string }> {
  const res = await request<
    { message: string } | ApiEnvelope<{ message: string }>
  >({
    method: "DELETE",
    url: `${LEADS_BASE}/${id}`,
  });

  return unwrap<{ message: string }>(res);
}

/** 🔹 Transfer a single lead product to another user */
export async function transferLeadProduct(
  leadProductId: number,
  body: TransferLeadProductBody
): Promise<LeadProductWithMeta> {
  const dto: any = { ...body };

  if (dto.reasonText != null) dto.reasonText = String(dto.reasonText).trim();

  const res = await request<
    LeadProductWithMeta | ApiEnvelope<LeadProductWithMeta>
  >({
    method: "POST",
    url: `${LEADS_BASE}/products/${leadProductId}/transfer`,
    data: dto,
  });

  return unwrap<LeadProductWithMeta>(res);
}

export async function listLeadProductMilestones(
  leadProductId: number
): Promise<LeadProductMilestone[]> {
  const res = await request<
    LeadProductMilestone[] | ApiEnvelope<LeadProductMilestone[]>
  >({
    method: "GET",
    url: `${LEADS_BASE}/products/${leadProductId}/milestones`,
  });

  return unwrap<LeadProductMilestone[]>(res);
}

export async function createLeadProductMilestone(
  leadProductId: number,
  body: CreateLeadProductMilestoneBody
): Promise<LeadProductMilestone> {
  const dto: any = { ...body };
  if (dto.note != null) dto.note = String(dto.note).trim();
  if (dto.countryIso2 != null) dto.countryIso2 = String(dto.countryIso2).trim().toUpperCase();

  const res = await request<
    LeadProductMilestone | ApiEnvelope<LeadProductMilestone>
  >({
    method: "POST",
    url: `${LEADS_BASE}/products/${leadProductId}/milestones`,
    data: dto,
  });

  return unwrap<LeadProductMilestone>(res);
}

export async function updateLeadProductMilestone(
  leadProductId: number,
  milestoneId: number,
  body: UpdateLeadProductMilestoneBody
): Promise<LeadProductMilestone> {
  const dto: any = { ...body };
  if (dto.note != null) dto.note = String(dto.note).trim();
  if (dto.countryIso2 != null) dto.countryIso2 = String(dto.countryIso2).trim().toUpperCase();

  const res = await request<
    LeadProductMilestone | ApiEnvelope<LeadProductMilestone>
  >({
    method: "PUT",
    url: `${LEADS_BASE}/products/${leadProductId}/milestones/${milestoneId}`,
    data: dto,
  });

  return unwrap<LeadProductMilestone>(res);
}

export async function deleteLeadProductMilestone(
  leadProductId: number,
  milestoneId: number
): Promise<{ message?: string }> {
  const res = await request<
    { message?: string } | ApiEnvelope<{ message?: string }>
  >({
    method: "DELETE",
    url: `${LEADS_BASE}/products/${leadProductId}/milestones/${milestoneId}`,
  });

  return unwrap<{ message?: string }>(res);
}

export async function getLeadProductVisaDetails(
  leadProductId: number
): Promise<LeadProductVisaDetail | null> {
  const res = await request<
    LeadProductVisaDetail | null | ApiEnvelope<LeadProductVisaDetail | null>
  >({
    method: "GET",
    url: `${LEADS_BASE}/products/${leadProductId}/visa-details`,
  });

  return unwrap<LeadProductVisaDetail | null>(res);
}

export async function upsertLeadProductVisaDetails(
  leadProductId: number,
  body: UpsertLeadProductVisaDetailBody
): Promise<LeadProductVisaDetail> {
  const dto: any = { ...body };

  if (dto.countryIso2 != null)
    dto.countryIso2 = String(dto.countryIso2).trim().toUpperCase();
  if (dto.loginId != null) dto.loginId = String(dto.loginId).trim();
  if (dto.loginPassword != null)
    dto.loginPassword = String(dto.loginPassword).trim();
  if (dto.remark != null) dto.remark = String(dto.remark).trim();

  const res = await request<
    LeadProductVisaDetail | ApiEnvelope<LeadProductVisaDetail>
  >({
    method: "PUT",
    url: `${LEADS_BASE}/products/${leadProductId}/visa-details`,
    data: dto,
  });

  return unwrap<LeadProductVisaDetail>(res);
}
